(ns client-portal.client.system-configuration.data
  (:require [client-portal.client.concepts.data :as concepts.d]
            [clojure.set :as set]
            [clojure.string :as s]
            [shared.core.account.data :as account.d]
            [shared.core.reactive :as re]
            [shared.core.router :as router]
            [shared.core.util :as util]))

(def root-path [:system-configuration])
(def namespaces-path (conj root-path :namespaces))
(def schema-path (conj root-path :schema))
(def stored-path (conj root-path :stored))
(def inherited-path (conj root-path :inherited))
(def ingress-identifiers-path (conj root-path :ingress-identifiers))
(def form-path (conj root-path :form))
(def save-path (conj root-path :save))
(def expanded-groups-path (conj root-path :expanded-groups))

;; `occasions` drives the pay app's occasion overrides and isn't edited here
(def namespace-options
  {"pay" {:hidden-fields #{:occasions}}
   "sales_smart_ordering_demo" {:concept-only? true}})

(def namespace-blacklist #{"menu"})

(defn namespace-label [namespace]
  (some-> namespace
          (util/->capitalize-words)
          (s/replace #"\b(Oasys|Pos|Vi)\b" #(s/upper-case (first %)))))

;; The page's own `sc-` query params, kept apart from the shared `cid`/`lid` header filters. Without its own concept it
;; starts on the layout's, which it only reads.
(defn current-scope [db]
  (when-let [namespace (:namespace (router/current-path-params db))]
    (let [{:keys [sc-cid sc-coid sc-lid sc-iid]} (router/current-query-params db)
          scoped? (not (get-in namespace-options [namespace :concept-only?]))
          location-id (when scoped? (util/parse-int sc-lid))]
      {:namespace namespace
       :concept-id (or (util/parse-int sc-cid)
                       (concepts.d/current-concept-id db))
       :cohort-id (when scoped? (util/parse-int sc-coid))
       :location-id location-id
       :ingress-identifier (when location-id
                             (some-> sc-iid (str) (not-empty)))})))

(defn scope-level [{:keys [cohort-id location-id ingress-identifier]}]
  (cond
    ingress-identifier :ingress-identifier
    location-id :location
    cohort-id :cohort
    :else :concept))

(defn concept-level? [db]
  (-> (current-scope db) (scope-level) (= :concept)))

(defn field-path [{:keys [parent-path field-name]}]
  (conj (or parent-path []) field-name))

(defn leaf-fields [fields]
  (mapcat #(if (= (:field-type %) :map)
             (leaf-fields (:map-values %))
             [%])
          fields))

(defn object-vector-field? [{:keys [field-type vector-type]}]
  (and (= field-type :vector) (= (:field-type vector-type) :map)))

(defn string-list-field? [{:keys [field-type vector-type]}]
  (and (= field-type :vector) (= (:field-type vector-type) :string)))

(defn ->field
  "A malli entry (`[name props? type]`) as the field the form renders"
  [[field-name & entry] nesting-idx parent-path]
  (let [[props field-type] (if (map? (first entry)) entry [nil (first entry)])
        kind (when (vector? field-type) (first field-type))
        children (when kind
                   (cond->> (rest field-type)
                            (map? (second field-type)) (rest)))
        child-path (conj (or parent-path []) field-name)]
    (merge props
           {:field-name field-name
            :nesting-idx nesting-idx
            :parent-path parent-path}
           (case kind
             nil {:field-type field-type}
             :enum {:field-type :enum
                    :enum-values (vec children)}
             :map {:field-type :map
                   :map-values (mapv #(->field % (inc nesting-idx) child-path) children)}
             :set {:field-type :set
                   :set-type (->field [nil (first children)] 0 [])}
             (:vector :sequential) {:field-type :vector
                                    :vector-type (->field [nil (first children)] 0 [])}
             (:or :and :maybe) {:field-type kind}
             (merge {:field-type kind}
                    (when (map? (second field-type))
                      (second field-type)))))))

(defn schema->fields [schema]
  (when (vector? schema)
    (->> (cond->> (rest schema)
                  (map? (second schema)) (rest))
         (mapv #(->field % 0 [])))))

(re/reg-auth0-loader
  :system-configuration/load-schema
  (fn [[namespace]]
    (conj schema-path namespace))
  (fn [_ namespace]
    {:method :get
     :url "/api/system-configuration/schema"
     :query-params {:namespace namespace}})
  :transf (fn [resp]
            (or (try
                  (some-> resp :body (schema->fields))
                  (catch :default _
                    nil))
                []))
  :error-transf (fn [] []))

(re/reg-sub
  :system-configuration/schema-
  (fn [db [_ namespace]]
    (when namespace
      (or (get-in db (conj schema-path namespace))
          (re/dispatch-load [:system-configuration/load-schema namespace])))))

(defn schema-fields [db]
  (some->> (current-scope db) :namespace (conj schema-path) (get-in db) (util/when-not-loading)))

(re/reg-auth0-loader
  :system-configuration/load-namespaces
  namespaces-path
  (fn [_ _]
    {:method :get
     :url "/api/system-configuration/namespaces"})
  :transf (fn [resp]
            (or (some->> resp :body (remove namespace-blacklist) (sort) (vec))
                []))
  :error-transf (fn [] []))

(re/reg-sub
  :system-configuration/namespaces-
  (fn [db]
    (when-let [roles (account.d/account-user-roles db)]
      (if (contains? roles :system-configuration-admin)
        (or (get-in db namespaces-path)
            (re/dispatch-load [:system-configuration/load-namespaces]))
        []))))

(re/reg-sub :system-configuration/scope- current-scope)

(re/reg-sub
  :system-configuration/level-
  :<- [:system-configuration/scope-]
  (fn [scope]
    (scope-level scope)))

(defn scope->query-params [scope]
  (util/->map-without-nil-vals scope))

(re/reg-auth0-loader
  :system-configuration/load-ingress-identifiers
  (fn [[concept-id location-id]]
    (conj ingress-identifiers-path concept-id location-id))
  (fn [_ concept-id location-id]
    {:method :get
     :url "/api/system-configuration/ingress-identifiers"
     :query-params {:concept-id concept-id
                    :location-id location-id}})
  :transf (fn [resp]
            (or (some-> resp :body (vec))
                []))
  :error-transf (fn [] []))

(re/reg-sub
  :system-configuration/ingress-identifiers-
  (fn [db]
    (let [{:keys [concept-id location-id]} (current-scope db)]
      (when (and concept-id location-id)
        (or (get-in db (conj ingress-identifiers-path concept-id location-id))
            (re/dispatch-load [:system-configuration/load-ingress-identifiers concept-id location-id]))))))

(defn vec-of-maps->form-rows
  "Adds a stable :id to each row so the editor can key off it"
  [vs]
  (->> (or vs [])
       (map-indexed (fn [idx v]
                      (if (map? v)
                        (assoc v :id (or (:id v) (str "row-" idx)))
                        {:id (str "row-" idx)})))
       (vec)))

(defn form-rows->vec-of-maps
  "Strips :id and drops rows whose fields are all blank strings (when every field is a string)"
  [rows inner-fields]
  (let [field-names (mapv :field-name inner-fields)
        all-string? (and (not-empty field-names)
                         (every? #(= (:field-type %) :string) inner-fields))]
    (->> (or rows [])
         (mapv #(select-keys % field-names))
         (remove (fn [row]
                   (and all-string?
                        (every? #(s/blank? (get row %)) field-names))))
         (vec))))

(defn strings->form-rows [vs]
  (->> (or vs [])
       (map-indexed (fn [idx v]
                      (if (and (map? v) (contains? v :value))
                        {:id (or (:id v) (str "row-" idx))
                         :value (or (:value v) "")}
                        {:id (str "row-" idx)
                         :value (if (string? v) v "")})))
       (vec)))

(defn form-rows->strings [rows]
  (->> (or rows [])
       (keep #(if (map? %) (:value %) %))
       (remove s/blank?)
       (mapv str)))

(defn raw->form-value [field v]
  (cond
    (and (object-vector-field? field) (sequential? v)) (vec-of-maps->form-rows v)
    (and (string-list-field? field) (sequential? v)) (strings->form-rows v)
    :else v))

(defn config->form-config [config fields]
  (reduce (fn [m {:keys [field-name field-type map-values] :as field}]
            (let [v (get config field-name)]
              (cond
                (nil? v) m
                (= field-type :map) (assoc m field-name (config->form-config v map-values))
                :else (assoc m field-name (raw->form-value field v)))))
          (or config {})
          fields))

(defn collect-disabled-map-paths
  "Map fields saved as an explicit null at this level, i.e. disabled here"
  [config fields]
  (->> fields
       (filter #(= (:field-type %) :map))
       (mapcat (fn [{:keys [field-name map-values] :as field}]
                 (let [v (get config field-name)]
                   (cond
                     (and (contains? config field-name) (nil? v)) [(field-path field)]
                     (map? v) (collect-disabled-map-paths v map-values)))))
       (set)))

(defn- overwrite-entry [override-states parent-path [k v]]
  (let [p (conj parent-path k)]
    (if (map? v)
      (some->> v
               (keep #(overwrite-entry override-states p %))
               (not-empty)
               (into {})
               (vector k))
      (when (= :overwrite (get-in override-states p))
        [k v]))))

(defn overwrite-projection
  "The form values the user is overwriting at this level, which is what a change is measured against"
  [values override-states]
  (->> values
       (keep #(overwrite-entry override-states [] %))
       (into {})))

(defn- groups-with-values
  "Groups with something saved inside them at this level, which start open"
  [config fields]
  (->> fields
       (filter #(= (:field-type %) :map))
       (mapcat (fn [{:keys [field-name map-values] :as field}]
                 (let [v (get config field-name)]
                   (when (and (map? v) (not-empty v))
                     (cons (field-path field) (groups-with-values v map-values))))))
       (set)))

(defn init-form [db scope config]
  (let [fields (schema-fields db)
        values (config->form-config config fields)
        concept-level? (= :concept (scope-level scope))
        override-states (reduce (fn [states field]
                                  (let [p (field-path field)]
                                    (assoc-in states p (if (or concept-level? (util/path-exists? config p))
                                                         :overwrite
                                                         :inherit))))
                                {}
                                (leaf-fields fields))
        disabled-fields (collect-disabled-map-paths config fields)]
    (assoc-in db form-path {:scope scope
                            :values values
                            :original-values (overwrite-projection values override-states)
                            :override-states override-states
                            :disabled-fields disabled-fields
                            :original-disabled-fields disabled-fields
                            :expanded-groups (groups-with-values config fields)})))

(re/reg-auth0-loader
  :system-configuration/load-stored
  (fn [[scope]]
    (conj stored-path scope))
  (fn [_ scope]
    {:method :get
     :url "/api/system-configuration"
     :query-params (scope->query-params scope)})
  :transf (fn [resp]
            (or (some-> resp :body :configuration (util/json-string->map))
                {}))
  :error-transf (fn [] :error)
  :on-success (fn [db resp [scope]]
                (cond-> db
                        (= scope (current-scope db))
                        (init-form scope (or (some-> resp :body :configuration (util/json-string->map))
                                             {})))))

(re/reg-sub
  :system-configuration/stored-
  (fn [db [_ scope]]
    (or (get-in db (conj stored-path scope))
        (re/dispatch-load [:system-configuration/load-stored scope]))))

(re/reg-auth0-loader
  :system-configuration/load-inherited
  (fn [[scope]]
    (conj inherited-path scope))
  (fn [_ scope]
    {:method :get
     :url "/api/system-configuration/inherited"
     :query-params (scope->query-params scope)})
  :transf (fn [resp]
            (or (some-> resp :body :configuration (util/->kebab-case-keywords))
                {}))
  :error-transf (fn [] {}))

(defn inherited [db]
  (util/when-not-loading (get-in db (conj inherited-path (current-scope db)))))

(re/reg-sub
  :system-configuration/inherited-
  (fn [db [_ scope]]
    (or (get-in db (conj inherited-path scope))
        (re/dispatch-load [:system-configuration/load-inherited scope]))))

;; Cleared on entering and leaving a scope, so each visit refetches and reflects saves made at its parents meanwhile
(re/reg-event-db
  :system-configuration/clear-scope re/middleware
  (fn [db [scope]]
    (cond-> (-> db
                (update-in stored-path dissoc scope)
                (update-in inherited-path dissoc scope))
            (= scope (get-in db (conj form-path :scope))) (assoc-in form-path nil))))

(re/reg-sub
  :system-configuration/form-ready?-
  (fn [db [_ scope]]
    (= scope (get-in db (conj form-path :scope)))))

(defn override-state [db field]
  (get-in db
          (into (conj form-path :override-states) (field-path field))
          (if (concept-level? db) :overwrite :inherit)))

(re/reg-sub :system-configuration/override-state- (fn [db [_ field]] (override-state db field)))

(re/reg-sub
  :system-configuration/field-value-
  (fn [db [_ field]]
    (if (= :inherit (override-state db field))
      (get-in (inherited db) (field-path field))
      (get-in db (into (conj form-path :values) (field-path field))))))

(re/reg-sub
  :system-configuration/map-field-disabled?-
  (fn [db [_ field]]
    (contains? (get-in db (conj form-path :disabled-fields)) (field-path field))))

(defn overwrite-field [db field]
  (let [values-path (into (conj form-path :values) (field-path field))
        value (->> [(get-in db values-path)
                    (raw->form-value field (get-in (inherited db) (field-path field)))
                    (when (= (:field-type field) :boolean) false)]
                   (remove nil?)
                   (first))]
    (-> db
        (assoc-in values-path value)
        (assoc-in (into (conj form-path :override-states) (field-path field)) :overwrite))))

(defn inherit-field [db field]
  (assoc-in db (into (conj form-path :override-states) (field-path field)) :inherit))

(re/reg-event-db
  :system-configuration/overwrite-field re/middleware
  (fn [db [field]]
    (overwrite-field db field)))

(re/reg-event-db
  :system-configuration/inherit-field re/middleware
  (fn [db [field]]
    (inherit-field db field)))

;; `hidden-fields` aren't rendered, so bulk toggles leave them as loaded
(re/reg-event-db
  :system-configuration/set-all-fields re/middleware
  (fn [db [overwrite? hidden-fields]]
    (cond-> (->> (schema-fields db)
                 (remove #(contains? hidden-fields (:field-name %)))
                 (leaf-fields)
                 (reduce (if overwrite? overwrite-field inherit-field) db))
            (not overwrite?) (update-in (conj form-path :disabled-fields)
                                        #(set (filter (fn [p] (contains? hidden-fields (first p))) %))))))

;; A map disabled below the concept level saves as an explicit null so it overrides, rather than inherits, the parent's
(re/reg-event-db
  :system-configuration/set-map-field-disabled re/middleware
  (fn [db [field disabled?]]
    (update-in db
               (conj form-path :disabled-fields)
               (fnil (if disabled? conj disj) #{})
               (field-path field))))

;; Groups opened or closed by hand are kept per namespace in local storage, over the ones that start open
(defn- group-expanded? [db field]
  (let [p (field-path field)
        toggled (get-in (:local-storage db) (conj expanded-groups-path (:namespace (current-scope db))))]
    (if (contains? toggled p)
      (get toggled p)
      (contains? (get-in db (conj form-path :expanded-groups)) p))))

(re/reg-sub :system-configuration/group-expanded?- (fn [db [_ field]] (group-expanded? db field)))

(re/reg-event-fx
  :system-configuration/toggle-group re/middleware
  (fn [{:keys [db local-storage]} [field]]
    {:local-storage (assoc-in local-storage
                              (conj expanded-groups-path (:namespace (current-scope db)) (field-path field))
                              (not (group-expanded? db field)))}))

(re/reg-event-db
  :system-configuration/change-field-value re/middleware
  (fn [db [{:keys [field-type optional] :as field} value]]
    (assoc-in db
              (into (conj form-path :values) (field-path field))
              (when-not (and optional (= field-type :string) (= value ""))
                value))))

(defn field-value-valid? [{:keys [field-type optional min max vector-type] :as field} v]
  (cond
    (and optional (or (nil? v) (= v ""))) true

    (contains? #{:int :double :float} field-type)
    (and (number? v)
         (js/isFinite v)
         (or (not= field-type :int) (integer? v))
         (or (nil? min) (>= v min))
         (or (nil? max) (<= v max)))

    (string? v) (or optional (not-empty v))

    (object-vector-field? field)
    (or optional (not-empty (form-rows->vec-of-maps v (:map-values vector-type))))

    (string-list-field? field)
    (or optional (not-empty (form-rows->strings v)))

    :else (some? v)))

(defn- fields-valid? [{:keys [concept-level? values override-states disabled-fields] :as args} fields]
  (every? (fn [{:keys [field-type map-values] :as field}]
            (let [p (field-path field)]
              (cond
                (= field-type :map) (or (contains? disabled-fields p)
                                        (fields-valid? args map-values))
                (and (not concept-level?) (= :inherit (get-in override-states p))) true
                :else (field-value-valid? field (get-in values p)))))
          fields))

(re/reg-sub
  :system-configuration/valid?-
  (fn [db]
    (let [{:keys [values override-states disabled-fields]} (get-in db form-path)]
      (fields-valid? {:concept-level? (concept-level? db)
                      :values values
                      :override-states override-states
                      :disabled-fields (or disabled-fields #{})}
                     (schema-fields db)))))

(re/reg-sub
  :system-configuration/field-valid?-
  (fn [db [_ field]]
    (or (= :inherit (override-state db field))
        (field-value-valid? field (get-in db (into (conj form-path :values) (field-path field)))))))

(defn- leaf-paths [m parent-path]
  (mapcat (fn [[k v]]
            (if (and (map? v) (not-empty v))
              (leaf-paths v (conj parent-path k))
              [(conj parent-path k)]))
          m))

(defn- changed-value-paths [{:keys [values original-values override-states]}]
  (let [current-values (overwrite-projection values override-states)]
    (->> (concat (leaf-paths current-values []) (leaf-paths original-values []))
         (distinct)
         (remove #(and (= (util/path-exists? current-values %) (util/path-exists? original-values %))
                       (= (get-in current-values %) (get-in original-values %)))))))

(defn- toggled-group-paths [{:keys [disabled-fields original-disabled-fields]}]
  (set/difference (set/union disabled-fields original-disabled-fields)
                  (set/intersection disabled-fields original-disabled-fields)))

(re/reg-sub
  :system-configuration/unsaved-count-
  (fn [db]
    (let [form (get-in db form-path)]
      (+ (count (changed-value-paths form))
         (count (toggled-group-paths form))))))

(defn- display-value [field v]
  (cond
    (object-vector-field? field) (form-rows->vec-of-maps v (-> field :vector-type :map-values))
    (string-list-field? field) (form-rows->strings v)
    :else v))

(re/reg-sub
  :system-configuration/changes-
  (fn [db]
    (let [{:keys [values original-values override-states disabled-fields] :as form} (get-in db form-path)
          concept? (concept-level? db)
          inherited-config (inherited db)
          current-values (overwrite-projection values override-states)
          value-paths (changed-value-paths form)
          group-paths (toggled-group-paths form)
          side (fn [field projection]
                 (let [p (field-path field)
                       v (get-in projection p)]
                   (cond
                     (and (util/path-exists? projection p) (or (some? v) (not concept?))) {:value (display-value field v)
                                                                                          :set-here? true}
                     (not concept?) {:value (get-in inherited-config p)
                                     :inherited? true})))
          walk (fn walk [fields]
                 (mapcat (fn [{:keys [field-type map-values] :as field}]
                           (let [p (field-path field)]
                             (if (= field-type :map)
                               (cond->> (walk map-values)
                                        (contains? group-paths p) (cons {:path p
                                                                         :kind (if (contains? disabled-fields p)
                                                                                 :turned-off
                                                                                 :turned-on)}))
                               (when (some #(= p (take (count p) %)) value-paths)
                                 (let [before (side field original-values)
                                       after (side field current-values)]
                                   [{:path p
                                     :before before
                                     :after after
                                     :kind (cond
                                             (and concept? (nil? before)) :added
                                             (and concept? (nil? after)) :cleared
                                             (and (:set-here? before) (:set-here? after)) :changed
                                             (:set-here? after) :overridden
                                             :else :inherited)}])))))
                         fields))]
      (walk (schema-fields db)))))

(defn- override-counts
  "A group turned off at this level counts as one overridden setting in place of its children"
  [db fields]
  (let [disabled-fields (get-in db (conj form-path :disabled-fields) #{})]
    (reduce (fn [counts {:keys [field-type map-values] :as field}]
              (cond
                (and (= field-type :map) (contains? disabled-fields (field-path field)))
                (-> counts (update :overridden inc) (update :total inc))

                (= field-type :map)
                (merge-with + counts (override-counts db map-values))

                :else
                (cond-> (update counts :total inc)
                        (= :overwrite (override-state db field)) (update :overridden inc))))
            {:overridden 0
             :total 0}
            fields)))

(re/reg-sub
  :system-configuration/override-counts-
  (fn [db]
    (let [hidden-fields (get-in namespace-options [(:namespace (current-scope db)) :hidden-fields] #{})]
      (->> (schema-fields db)
           (remove #(contains? hidden-fields (:field-name %)))
           (override-counts db)))))

(re/reg-event-db
  :system-configuration/discard-changes re/middleware
  (fn [db]
    (let [scope (get-in db (conj form-path :scope))]
      (init-form db scope (get-in db (conj stored-path scope))))))

(re/reg-sub
  :system-configuration/group-summary-
  (fn [db [_ {:keys [map-values]}]]
    (assoc (override-counts db map-values)
      :invalid (->> (leaf-fields map-values)
                    (remove #(= :inherit (override-state db %)))
                    (remove #(field-value-valid? % (get-in db (into (conj form-path :values) (field-path %)))))
                    (count)))))

(defn- field->config-entries
  "[path value] pairs to save. Below the concept level an empty value is an explicit null, overriding the parent's"
  [{:keys [concept-level? values override-states disabled-fields] :as args}
   {:keys [optional field-type map-values vector-type] :as field}]
  (let [p (field-path field)]
    (cond
      (and (= field-type :map) (not concept-level?) (contains? disabled-fields p))
      [[p nil]]

      (= field-type :map)
      (mapcat #(field->config-entries args %) map-values)

      (= :overwrite (get-in override-states p))
      (let [v (cond
                (object-vector-field? field) (form-rows->vec-of-maps (get-in values p) (:map-values vector-type))
                (string-list-field? field) (form-rows->strings (get-in values p))
                :else (get-in values p))
            empty-value? (or (and optional (= v ""))
                             (and (or (object-vector-field? field) (string-list-field? field))
                                  (empty? v)))]
        (cond
          (not concept-level?) [[p (when-not empty-value? v)]]
          empty-value? nil
          (= field-type :boolean) [[p (boolean v)]]
          (some? v) [[p v]])))))

(defn configuration-for-save [db]
  (let [{:keys [values override-states disabled-fields]} (get-in db form-path)]
    (->> (schema-fields db)
         (mapcat #(field->config-entries {:concept-level? (concept-level? db)
                                          :values values
                                          :override-states override-states
                                          :disabled-fields (or disabled-fields #{})}
                                         %))
         (reduce (fn [config [p v]]
                   (assoc-in config p v))
                 {}))))

(re/auth0-http-request
  :system-configuration/save
  save-path
  (fn [db _]
    (let [configuration (configuration-for-save db)]
      {:method :post
       :url "/api/system-configuration"
       :json-params (assoc (scope->query-params (get-in db (conj form-path :scope)))
                      :configuration (some-> configuration
                                             (not-empty)
                                             (util/map->json-string-min)))}))
  :on-success (fn [db _ _]
                (re/dispatch [:toasts/add-info {:msg "Changes saved"
                                                :duration 3000}])
                (let [{:keys [concept-id location-id ingress-identifier]} (get-in db (conj form-path :scope))]
                  (cond-> (update-in db form-path (fn [{:keys [values override-states disabled-fields] :as form}]
                                                    (assoc form
                                                      :original-values (overwrite-projection values override-states)
                                                      :original-disabled-fields disabled-fields)))
                          ;; A save can add (or delete) an identifier's only row, so refetch the location's list
                          ingress-identifier (update-in (conj ingress-identifiers-path concept-id) dissoc location-id))))
  :on-fail (fn [db {:keys [body]} _]
             (re/dispatch [:toasts/add-error {:msg (or (:message body)
                                                       "Configuration failed validation and was not saved")
                                              :duration 5000}])
             db))

(re/reg-sub
  :system-configuration/saving?-
  (fn [db]
    (get-in db (conj save-path :request-active?))))
